import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import clsx from 'clsx'
import { Button } from '../common/Button'
import { Task } from '../../lib/types/database.types'
import {
  TASK_PRIORITY_COLORS,
  TASK_PRIORITY_LABELS,
  TASK_STATUS_COLORS,
  TASK_STATUS_LABELS,
  TASK_TYPE_COLORS,
  TASK_TYPE_LABELS,
} from '../../lib/config/project-config'
import { stripHtml, truncateText } from '../../utils/formatters'

interface KanbanCardProps {
  task: Task
  profileMap: Map<string, string>
  canAssign?: boolean
  onAssign?: (task: Task) => void
  onView?: (task: Task) => void
  readinessState?: 'ready' | 'blocked' | null
}

export const KanbanCard: React.FC<KanbanCardProps> = ({ task, profileMap, canAssign = false, onAssign, onView, readinessState = null }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const statusColor = TASK_STATUS_COLORS[task.status as keyof typeof TASK_STATUS_COLORS] || '#6b7280'
  const typeColor = TASK_TYPE_COLORS[task.type as keyof typeof TASK_TYPE_COLORS] || '#6b7280'
  const priorityColor = TASK_PRIORITY_COLORS[task.priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280'

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'cursor-default rounded-xl border border-stone-300 bg-stone-100 p-3 shadow-sm transition',
        isDragging ? 'opacity-50 shadow-lg' : 'hover:shadow-md',
        task.parent_task_id && 'ml-4 border-l-4 border-l-amber-400'
      )}
    >
      {/* Sub-task Indicator */}
      {task.parent_task_id && (
        <div className="mb-2 text-xs text-amber-600">
          <span className="font-medium">→ Sub-task</span>
          {readinessState === 'ready' && (
            <span className="ml-2 inline-flex items-center rounded-full border border-green-300 bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-800">
              ✓ Ready
            </span>
          )}
          {readinessState === 'blocked' && (
            <span className="ml-2 inline-flex items-center rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
              ✗ Blocked
            </span>
          )}
        </div>
      )}

      {/* Drag Handle + Title */}
      <div className="mb-2 flex items-start gap-2">
        <div
          {...attributes}
          {...listeners}
          className="mt-0.5 cursor-grab active:cursor-grabbing flex-shrink-0 text-gray-400 hover:text-gray-600"
          title="Drag to move"
        >
          ⋮⋮
        </div>
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-xs font-semibold text-slate-500">{task.unique_task_id}</p>
          <h4 className="line-clamp-2 font-medium text-slate-900">{task.title}</h4>
        </div>
      </div>

      {/* Description (if present) */}
      {task.description && (
        <p className="mb-3 line-clamp-2 text-xs text-slate-600">{truncateText(stripHtml(task.description), 100)}</p>
      )}

      {/* Metadata Badges */}
      <div className="flex flex-wrap gap-2">
        {/* Status Badge */}
        <span
          className="inline-flex rounded-full border px-2.5 py-1 text-xs font-medium"
          style={{ borderColor: statusColor, color: statusColor }}
        >
          {TASK_STATUS_LABELS[task.status as keyof typeof TASK_STATUS_LABELS] || task.status}
        </span>

        {/* Type Badge */}
        <span
          className="inline-flex rounded-full border px-2.5 py-1 text-xs font-medium"
          style={{ borderColor: typeColor, color: typeColor }}
        >
          {TASK_TYPE_LABELS[task.type as keyof typeof TASK_TYPE_LABELS] || task.type}
        </span>

        <span
          className="inline-flex rounded-full border px-2.5 py-1 text-xs font-medium"
          style={{ borderColor: priorityColor, color: priorityColor }}
        >
          {TASK_PRIORITY_LABELS[task.priority as keyof typeof TASK_PRIORITY_LABELS] || task.priority}
        </span>

        {/* Estimated Hours Badge */}
        {task.estimated_hours && (
          <span className="inline-flex rounded-full border border-blue-400 px-2.5 py-1 text-xs font-medium text-blue-600">
            {task.estimated_hours}h
          </span>
        )}
      </div>

      {/* Assignee Info */}
      {task.assigned_to && (
        <div className="mt-3 border-t border-stone-200 pt-2">
          <p className="text-xs text-slate-600">
            <span className="font-medium">Assigned to:</span> {profileMap.get(task.assigned_to) || 'Unknown User'}
          </p>
        </div>
      )}

      {!task.assigned_to && canAssign && onAssign && (
        <div className="mt-3 border-t border-stone-200 pt-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onPointerDown={(event: React.PointerEvent<HTMLButtonElement>) => event.stopPropagation()}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()
              onAssign(task)
            }}
          >
            Assign
          </Button>
        </div>
      )}

      {onView && (
        <div className="mt-3 border-t border-stone-200 pt-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onPointerDown={(event: React.PointerEvent<HTMLButtonElement>) => event.stopPropagation()}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()
              onView(task)
            }}
          >
            View
          </Button>
        </div>
      )}

      {/* App Info */}
      <div className="mt-2">
        <p className="text-xs text-slate-500">{task.related_app}</p>
        {task.support_ticket_no ? <p className="text-xs text-slate-500">Ticket: {task.support_ticket_no}</p> : null}
      </div>
    </div>
  )
}

export default KanbanCard
